import React, { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function fmt(seconds) {
  if (!seconds || Number.isNaN(seconds)) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function AudioPlayer({ src, title, meta, darkMode, articleId }) {
  const audioRef = useRef(null);
  const pingedRef = useRef(false);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => () => audioRef.current?.pause(), []);

  const toggle = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) el.pause();
    else el.play();
  };

  const handlePlay = () => {
    setPlaying(true);
    if (!pingedRef.current && articleId) {
      pingedRef.current = true;
      fetch(`${API_URL}/api/articles/listen`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ article_id: articleId }),
      }).catch(() => {});
    }
  };

  const seek = (e) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
    el.currentTime = ratio * duration;
    setCurrent(el.currentTime);
  };

  return (
    <div data-testid="audio-player" className={`p-5 rounded-2xl border ${darkMode ? "bg-[#181C22] border-[#2A2F35]" : "bg-[#F4EFE6] border-[#E6DEC7]"}`}>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={handlePlay}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
      />
      <div className="flex items-center gap-4">
        <button
          data-testid="audio-play-toggle"
          onClick={toggle}
          aria-label={playing ? "Pause narration" : "Play narration"}
          className="w-12 h-12 shrink-0 rounded-full bg-[#B85D34] text-white flex items-center justify-center shadow-md hover:scale-105 transition-transform"
        >
          {playing ? <Pause size={19} /> : <Play size={19} className="ml-0.5" />}
        </button>
        <div className="flex-1 min-w-0">
          <h4 className="font-serif text-base font-medium truncate">Listen: {title}</h4>
          <p className="text-xs opacity-70">Studio narration (AI voice, not the author) · {meta}</p>
          <div
            data-testid="audio-progress-bar"
            onClick={seek}
            className={`mt-2.5 h-1.5 rounded-full cursor-pointer ${darkMode ? "bg-[#2A2F35]" : "bg-[#E6DEC7]"}`}
          >
            <div className="h-full rounded-full bg-[#B85D34] transition-[width] duration-200" style={{ width: duration ? `${(current / duration) * 100}%` : "0%" }} />
          </div>
        </div>
        <span className="text-xs font-mono opacity-70 shrink-0">{fmt(current)} / {fmt(duration)}</span>
      </div>
    </div>
  );
}
